import { Epic, ofType } from "redux-observable";
import { createSelector } from "reselect";
import { of } from "rxjs";
import { catchError, map, mergeMap, switchMap } from "rxjs/operators";
import { CustomerPortalState } from "..";
import { FileUploadResponse } from "../../../../../server/services/fileService";
import { NestedKeys } from "../../../../../types/HelperTypes";
import {
  FileUploadScope,
  FileUploadType,
  IContentChannel,
  NendaProduct,
  OverlayPosition,
} from "../../../../../types/NendaTypes";
import { contentChannelService } from "../../../../http/contentChannel.service";
import {
  AddContentChannelAction,
  AddContentChannelFailureAction,
  AddContentChannelSuccessAction,
  ClearFileUploadAction,
  CONTENTCHANNEL_ACTIONS,
  ContentChannelActions,
  DeleteContentChannelAction,
  DeleteContentChannelFailureAction,
  DeleteContentChannelSuccessAction,
  GetContentChannelsAction,
  GetContentChannelsFailureAction,
  GetContentChannelsSuccessAction,
  UpdateContentChannelAction,
  UpdateContentChannelFailureAction,
  UpdateContentChannelSuccessAction,
  UploadFileAction,
  UploadFileFailureAction,
  UploadLogoSuccessAction,
  UploadPosterSuccessAction,
} from "../../../../types/redux";
import {
  handleError,
  SetNotificationError,
  SetNotificationSuccess,
} from "./notificationReducer";
import { selectCurrentNavigationLevel } from "./workspaceReducer";
import { getCompanyIdByPremise } from "@client/utils/company";

export interface ContentChannelState {
  channels: IContentChannel[];
  isLoading: boolean;
  uploads: {
    poster: string | undefined;
    logo: string | undefined;
  };
}

export const initialContentChannelState: ContentChannelState = {
  channels: [],
  isLoading: false,
  uploads: {
    poster: undefined,
    logo: undefined,
  },
};

export function GetContentChannels(): GetContentChannelsAction {
  return { type: CONTENTCHANNEL_ACTIONS.GET_CONTENTCHANNELS };
}

function GetContentChannelsSuccess(
  channels: IContentChannel[]
): GetContentChannelsSuccessAction {
  return {
    type: CONTENTCHANNEL_ACTIONS.GET_CONTENTCHANNELS_SUCCESS,
    channels,
  };
}

function GetContentChannelsFailure(
  error: any
): GetContentChannelsFailureAction {
  return { type: CONTENTCHANNEL_ACTIONS.GET_CONTENTCHANNELS_FAILURE, error };
}

export function AddContentChannel(data): AddContentChannelAction {
  return { data, type: CONTENTCHANNEL_ACTIONS.ADD_CONTENTCHANNEL };
}

function AddContentChannelSuccess(
  channel: IContentChannel
): AddContentChannelSuccessAction {
  return {
    type: CONTENTCHANNEL_ACTIONS.ADD_CONTENTCHANNEL_SUCCESS,
    channel,
  };
}

function AddContentChannelFailure(error: any): AddContentChannelFailureAction {
  return { type: CONTENTCHANNEL_ACTIONS.ADD_CONTENTCHANNEL_FAILURE, error };
}

export function UpdateContentChannel(
  channel: Partial<IContentChannel>,
  unsetFields: NestedKeys<IContentChannel>[] = []
): UpdateContentChannelAction {
  return {
    request: {
      data: channel,
      unsetFields: unsetFields,
      expectedUpdatedAt: channel.updatedAt
        ? new Date(channel.updatedAt).toISOString()
        : undefined,
    },
    type: CONTENTCHANNEL_ACTIONS.UPDATE_CONTENTCHANNEL,
  };
}

function UpdateContentChannelSuccess(
  channel: IContentChannel
): UpdateContentChannelSuccessAction {
  return {
    type: CONTENTCHANNEL_ACTIONS.UPDATE_CONTENTCHANNEL_SUCCESS,
    channel,
  };
}

function UpdateContentChannelFailure(
  error: any
): UpdateContentChannelFailureAction {
  return { type: CONTENTCHANNEL_ACTIONS.UPDATE_CONTENTCHANNEL_FAILURE, error };
}

export function DeleteContentChannel(channelId): DeleteContentChannelAction {
  return { channelId, type: CONTENTCHANNEL_ACTIONS.DELETE_CONTENTCHANNEL };
}

function DeleteContentChannelSuccess(
  channel: IContentChannel
): DeleteContentChannelSuccessAction {
  return {
    type: CONTENTCHANNEL_ACTIONS.DELETE_CONTENTCHANNEL_SUCCESS,
    channel,
  };
}

function DeleteContentChannelFailure(
  error: any
): DeleteContentChannelFailureAction {
  return { type: CONTENTCHANNEL_ACTIONS.DELETE_CONTENTCHANNEL_FAILURE, error };
}

function UploadFileFailure(error: any): UploadFileFailureAction {
  return {
    type: CONTENTCHANNEL_ACTIONS.UPLOAD_FILE_FAILURE,
    error,
  };
}

export function UploadFile(
  channelId: string,
  image: File,
  scope: FileUploadScope
): UploadFileAction {
  return { channelId, image, type: CONTENTCHANNEL_ACTIONS.UPLOAD_LOGO, scope };
}

export function ClearUploadedFile(
  scope: FileUploadScope
): ClearFileUploadAction {
  return {
    type: CONTENTCHANNEL_ACTIONS.CLEAR_FILE_UPLOADS,
    scope,
  };
}

export function UploadLogoSuccess(
  channelId: string,
  uploadImageResponse: FileUploadResponse
): UploadLogoSuccessAction {
  return {
    channelId,
    uploadImageResponse,
    type: CONTENTCHANNEL_ACTIONS.UPLOAD_LOGO_SUCCESS,
  };
}
export function UploadPosterSuccess(
  channelId: string,
  uploadImageResponse: FileUploadResponse
): UploadPosterSuccessAction {
  return {
    channelId,
    uploadImageResponse,
    type: CONTENTCHANNEL_ACTIONS.UPLOAD_POSTER_SUCCESS,
  };
}

// Selectors
export const selectContentChannel = createSelector(
  [
    (state: CustomerPortalState) => state.contentChannel.channels,
    (_state, channelId: string) => channelId,
  ],
  (channels, channelId) => channels.find((c) => c._id === channelId)
);

export const selectContentChannelsByNavigatedPremise = createSelector(
  [
    (state: CustomerPortalState) => state.organizationUnits.premises.data,
    (state: CustomerPortalState) => state.contentChannel.channels,
    (state: CustomerPortalState) => state.workspace.scope.premise,
  ],
  (premisesData, channels, premiseId) => {
    const premise = premisesData.find((p) => p._id === premiseId);
    if (!premise) return [];
    return (
      channels.filter((c) => premise.contentChannels?.includes(c._id)) || []
    );
  }
);

export const selectContentChannelsByNavigatedCompany = createSelector(
  [
    (state: CustomerPortalState) => state.organizationUnits.premises.data,
    (state: CustomerPortalState) => state.contentChannel.channels,
    (state: CustomerPortalState) => state.workspace.scope.company,
  ],
  (premisesData, channels, companyId) => {
    const companyPremises = premisesData.filter(
      (p) =>
        getCompanyIdByPremise(p) === companyId &&
        p.nendaProducts.includes(NendaProduct.SIGNAGE)
    );

    if (!companyPremises.length) return [];
    // Only return channels that exists in all premises
    const commonChannels = channels.filter((c) =>
      companyPremises.every((p) => p.contentChannels?.includes(c._id))
    );

    return commonChannels;
  }
);

export const selectContentChannelsByScope = createSelector(
  [
    selectCurrentNavigationLevel,
    selectContentChannelsByNavigatedPremise,
    selectContentChannelsByNavigatedCompany,
  ],
  (scopeLevel, channelsByPremise, channelsByCompany) => {
    if (scopeLevel === "company") {
      return channelsByCompany;
    }
    if (scopeLevel === "premise") {
      return channelsByPremise;
    }
    return [];
  }
);

export const selectCategorizedContentChannelsByScope = createSelector(
  [selectContentChannelsByScope],
  (contentChannels) => {
    const categorizedContentChannels = contentChannels.reduce(
      (acc, channel) => {
        let category = channel.category;

        if (channel.playlist[0]?.type === "linearchannel") {
          category = "TV Channels";
        }

        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(channel);

        return acc;
      },
      {}
    );

    //Move TV Channels to the bottom
    const tvChannels = { ...categorizedContentChannels };
    if (categorizedContentChannels["TV Channels"]) {
      delete tvChannels["TV Channels"];

      tvChannels["TV Channels"] = categorizedContentChannels["TV Channels"];
    }

    return tvChannels;
  }
);

export const selectContentChannels = (
  state: CustomerPortalState
): IContentChannel[] => {
  return state.contentChannel.channels;
};

export const selectContentChannelById = createSelector(
  (state: CustomerPortalState) => state.contentChannel.channels,
  (_state: CustomerPortalState, channelId: string) => channelId,
  (channels, channelId) => channels.find((c) => c._id === channelId)
);

export const selectContentChannelsByIds = createSelector(
  (state: CustomerPortalState) => state.contentChannel.channels,
  (_state: CustomerPortalState, channelIds: string[]) => channelIds,
  (channels, channelIds) => {
    return channels.filter((c) => channelIds.includes(c._id));
  }
);

export const selectIsLoading = (state: CustomerPortalState) =>
  state.contentChannel.isLoading;

// Reducer
export default function contentChannelReducer(
  state: ContentChannelState = initialContentChannelState,
  action: ContentChannelActions
): ContentChannelState {
  switch (action.type) {
    case CONTENTCHANNEL_ACTIONS.GET_CONTENTCHANNELS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        channels: action.channels,
      };
    case CONTENTCHANNEL_ACTIONS.GET_CONTENTCHANNELS:
    case CONTENTCHANNEL_ACTIONS.ADD_CONTENTCHANNEL:
    case CONTENTCHANNEL_ACTIONS.UPDATE_CONTENTCHANNEL:
    case CONTENTCHANNEL_ACTIONS.DELETE_CONTENTCHANNEL:
    case CONTENTCHANNEL_ACTIONS.UPLOAD_LOGO:
      return {
        ...state,
        isLoading: true,
      };
    case CONTENTCHANNEL_ACTIONS.ADD_CONTENTCHANNEL_SUCCESS:
      return {
        ...state,
        channels: [...state.channels, action.channel],
        isLoading: false,
      };
    case CONTENTCHANNEL_ACTIONS.UPDATE_CONTENTCHANNEL_SUCCESS: {
      const updatedChannels = state.channels.map((c) => {
        if (c._id === action.channel._id) {
          return action.channel;
        }
        return c;
      });
      return {
        ...state,
        channels: updatedChannels,
        isLoading: false,
      };
    }
    case CONTENTCHANNEL_ACTIONS.DELETE_CONTENTCHANNEL_SUCCESS:
      return {
        ...state,
        channels: state.channels.filter((c) => c._id !== action.channel._id),
        isLoading: false,
      };
    case CONTENTCHANNEL_ACTIONS.CLEAR_FILE_UPLOADS:
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.scope]: undefined,
        },
      };
    case CONTENTCHANNEL_ACTIONS.UPLOAD_LOGO_SUCCESS: {
      if (!action.channelId) {
        return {
          ...state,
          uploads: {
            ...state.uploads,
            [FileUploadType.LOGO]: action.uploadImageResponse.fileUrl,
          },
        };
      }
      const updatedChannels = state.channels.map((c) => {
        if (c._id === action.channelId) {
          return {
            ...c,
            logo: {
              position: OverlayPosition.BOTTOM_LEFT,
              locator: action.uploadImageResponse.fileUrl,
              useInPlayer: true,
            },
          };
        }
        return c;
      });
      return {
        ...state,
        isLoading: false,
        channels: updatedChannels,
      };
    }
    case CONTENTCHANNEL_ACTIONS.UPLOAD_POSTER_SUCCESS: {
      if (!action.channelId) {
        return {
          ...state,
          uploads: {
            ...state.uploads,
            [FileUploadType.POSTER]: action.uploadImageResponse.fileUrl,
          },
        };
      }
      const updatedContentChannels = state.channels.map((c) => {
        if (c._id === action.channelId) {
          return {
            ...c,
            posterUrl: action.uploadImageResponse.fileUrl,
          };
        }
        return c;
      });
      return {
        ...state,
        isLoading: false,
        channels: updatedContentChannels,
      };
    }
    case CONTENTCHANNEL_ACTIONS.UPLOAD_FILE_FAILURE:
      return {
        ...state,
        isLoading: false,
      };

    default:
      return state;
  }
}

// Epics

const getContentChannels$: Epic = (action$) => {
  return action$.pipe(
    ofType(CONTENTCHANNEL_ACTIONS.GET_CONTENTCHANNELS),
    switchMap(() => {
      return contentChannelService.getChannels().pipe(
        map((channels) => {
          return GetContentChannelsSuccess(channels);
        }),
        catchError((err) => of([handleError, GetContentChannelsFailure(err)]))
      );
    })
  );
};

const addContentChannel$: Epic = (action$) => {
  return action$.pipe(
    ofType(CONTENTCHANNEL_ACTIONS.ADD_CONTENTCHANNEL),
    switchMap((a: AddContentChannelAction) => {
      return contentChannelService.createChannel(a.data).pipe(
        mergeMap((response: IContentChannel) => {
          return [
            GetContentChannels(),
            AddContentChannelSuccess(response),
            SetNotificationSuccess("Channel added"),
          ];
        }),
        catchError((err) => of([handleError, AddContentChannelFailure(err)]))
      );
    })
  );
};

const addContentChannelFailed$: Epic = (action$) => {
  return action$.pipe(
    ofType(CONTENTCHANNEL_ACTIONS.ADD_CONTENTCHANNEL_FAILURE),
    map((a) => SetNotificationError(a.data))
  );
};

const updateContentChannel$: Epic = (action$) => {
  return action$.pipe(
    ofType(CONTENTCHANNEL_ACTIONS.UPDATE_CONTENTCHANNEL),
    switchMap((a: UpdateContentChannelAction) => {
      return contentChannelService.updateChannel(a.request).pipe(
        mergeMap((response: IContentChannel) => {
          return [
            GetContentChannels(),
            UpdateContentChannelSuccess(response),
            SetNotificationSuccess("Channel updated"),
          ];
        }),
        catchError((err) => of(UpdateContentChannelFailure(err)))
      );
    })
  );
};

const updateContentChannelFailed$: Epic = (action$) => {
  return action$.pipe(
    ofType(CONTENTCHANNEL_ACTIONS.UPDATE_CONTENTCHANNEL_FAILURE),
    map((a: UpdateContentChannelFailureAction) =>
      SetNotificationError(a.error?.response?.message ?? a.error?.message)
    )
  );
};

const deleteContentChannel$: Epic = (action$) => {
  return action$.pipe(
    ofType(CONTENTCHANNEL_ACTIONS.DELETE_CONTENTCHANNEL),
    switchMap((a: DeleteContentChannelAction) => {
      return contentChannelService.deleteChannel(a.channelId).pipe(
        mergeMap((response: IContentChannel) => {
          return [
            GetContentChannels(),
            DeleteContentChannelSuccess(response),
            SetNotificationSuccess("Channel deleted"),
          ];
        }),
        catchError((err) => of([handleError, DeleteContentChannelFailure(err)]))
      );
    })
  );
};

const deleteContentChannelFailed$: Epic = (action$) => {
  return action$.pipe(
    ofType(CONTENTCHANNEL_ACTIONS.DELETE_CONTENTCHANNEL_FAILURE),
    map((a) => SetNotificationError(a.data))
  );
};

const uploadFile$: Epic = (action$) => {
  return action$.pipe(
    ofType(CONTENTCHANNEL_ACTIONS.UPLOAD_LOGO),
    switchMap((a: UploadFileAction) => {
      return contentChannelService
        .uploadFile(a.channelId, a.image, a.scope)
        .pipe(
          mergeMap((response) => {
            if (a.scope === FileUploadType.LOGO) {
              return [
                UploadLogoSuccess(a.channelId, response),
                SetNotificationSuccess("Logo uploaded"),
              ];
            }
            if (a.scope === FileUploadType.POSTER) {
              return [
                UploadPosterSuccess(a.channelId, response),
                SetNotificationSuccess("Poster uploaded"),
              ];
            }
            return [];
          }),
          catchError((err) => of([handleError, UploadFileFailure(err)]))
        );
    })
  );
};

export const contentChannelEpics = [
  getContentChannels$,
  addContentChannel$,
  addContentChannelFailed$,
  deleteContentChannel$,
  deleteContentChannelFailed$,
  updateContentChannel$,
  updateContentChannelFailed$,
  uploadFile$,
];
